import { installedItems } from "@p0security/shared/install/installed";
import type { BaseComponent } from "@p0security/shared/install/types";
import { assertNever } from "@p0security/shared/types/index";

export const DEFAULT_KUBERNETES_NAMESPACE = "p0-agentic-gateway";

// Read by .github/workflows/terraform-module-pin.yml, which greps this exact
// line and checks the version exists on the Terraform registry.
const AGENTIC_GATEWAY_MODULE_VERSION = "1.3.0";

/** The ai-gateway-stack chart the module installs. From module 1.3.0 the
 * module pins no chart, so a gateway release bumps this rather than the
 * module version. Must be an exact version: the module accepts no ranges. */
const AGENTIC_GATEWAY_CHART_VERSION = "1.4.0";

/** The chart's default `authServer.oidcIssuer`. */
export const GOOGLE_OIDC_ISSUER = "https://accounts.google.com";

/** Who authenticates the human at login — mirrors the chart's
 * `authServer.loginMode` (`oidc` federates upstream, `gcip` uses GCIP). */
export type GatewayLogin =
  | { mode: "gcip"; apiKey: string; projectId: string; tenantId: string }
  | { mode: "oidc"; oidcClientId: string; oidcIssuer?: string };

// Identity providers are their own top-level integration (see
// shared/integrations/resources/identity-provider), keyed by the "provider"
// component — not nested under this one, so this shape is duplicated
// (rather than imported) to accept both that integration's own config and
// the cross-integration snapshot a sibling installer sees
// (FrontendInstallContext.installed["identity-provider"]).
type IdentityProviderConfig = Record<
  "provider",
  Record<string, BaseComponent & { issuer?: string }>
>;

/** Deduplicates and sorts issuers so the rendered allowlist line is stable
 * regardless of the order providers were installed in. */
export const normalizeFederatedIssuers = (issuers: string[]): string[] =>
  [...new Set(issuers)].sort();

/** Distinct, sorted issuers of every installed identity provider — the set a
 * gateway's `federatedIssuerAllowlist` must contain today. */
export const installedFederatedIssuers = (
  config:
    | Partial<IdentityProviderConfig>
    | Record<string, Record<string, BaseComponent>>
    | undefined
): string[] => {
  const providers = installedItems(
    "provider",
    (config ?? {}) as IdentityProviderConfig
  );
  return normalizeFederatedIssuers(
    Object.values(providers)
      .map((provider) => provider?.issuer)
      .filter((issuer): issuer is string => !!issuer)
  );
};

/** Where the gateway keeps its data. Either a StorageClass the cluster
 * provides, used by the bundled PostgreSQL, VictoriaLogs, and Valkey alike, or
 * scratch space on a cluster that has none. Scratch space turns off the
 * chart's `persistence.enabled` on PostgreSQL and VictoriaLogs; Valkey has no
 * such switch, so the bundled one is turned off and the auth server caches in
 * memory only (see {@link renderCaches}). */
export type GatewayStorage =
  | { type: "noPersistence" }
  | { type: "storageClass"; name: string };

/** The chart's default `braekhus.tunnelHost`, the production API host. The
 * generated Terraform omits `tunnelHost` when it matches, so only a
 * non-production environment carries the override. */
const DEFAULT_TUNNEL_HOST = "api.p0.app";

export type GatewayTerraformInput = {
  gatewayId: string;
  orgSlug: string;
  url: string;
  letsEncryptEmail: string;
  login: GatewayLogin;
  storage: GatewayStorage;
  p0Url: string;
  /** Audience P0 verifies tokens against. Only a dev environment needs it:
   * there `p0Url` can be localhost while the audience is the real dev host.
   * Elsewhere the chart's default (`p0Url`) is right, so leave it undefined. */
  p0Audience?: string;
  serviceAccountEmail: string;
  kubernetesNamespace?: string;
  federatedIssuers: string[];
  /** Host the braekhus-client tunnels to (e.g. `api.p0.app`). Emitted only
   * when it differs from the chart's default, which is the production host. */
  tunnelHost: string;
};

/** Indent of the fields of the `authServer`, `braekhus` and `accessProxy`
 * blocks in the template below. */
const AUTH_SERVER_INDENT = " ".repeat(8);

/** The PostgreSQL and VictoriaLogs storage lines. Naming a class leaves the
 * chart on its default persistent path; noPersistence turns persistence off,
 * and the chart then requires no class at all. */
const renderStorage = (storage: GatewayStorage): string => {
  const { type } = storage;
  switch (type) {
    case "storageClass":
      return `storageClass = "${storage.name}"`;
    case "noPersistence":
      return `persistence = {
        enabled = false
      }`;
    default:
      return assertNever(type);
  }
};

/** Valkey has no `persistence.enabled`: it either gets a class or is turned
 * off, leaving the auth server to cache in memory (see {@link renderCaches}). */
const renderValkey = (storage: GatewayStorage): string => {
  const { type } = storage;
  switch (type) {
    case "storageClass":
      return `storageClass = "${storage.name}"`;
    case "noPersistence":
      return "enabled = false";
    default:
      return assertNever(type);
  }
};

/** Caches in memory only when the bundled Valkey is off. The chart's default
 * `caches` points at the bundled `valkey-primary` Service, which no longer
 * exists then, and the chart requires the value, so it can't simply be
 * dropped. The auth server needs no Valkey URL without a `valkey` tier. A
 * multi-line value, so it goes after the `=`-aligned fields. */
const renderCaches = (storage: GatewayStorage): string =>
  storage.type === "noPersistence"
    ? `
${AUTH_SERVER_INDENT}caches = jsonencode({
${AUTH_SERVER_INDENT}  defaults = [{ type = "memory" }]
${AUTH_SERVER_INDENT}})`
    : "";

/** Renders `key = "value"` lines, `=`-aligned on the longest key — needed
 * separately because authServer's two variants align differently. */
const renderAlignedFields = (
  indent: string,
  fields: readonly (readonly [string, string])[]
): string => {
  const width = Math.max(...fields.map(([key]) => key.length));
  return fields
    .map(([key, value]) => `${indent}${key.padEnd(width)} = "${value}"`)
    .join("\n");
};

/** The login-specific `authServer` values. Under `gcip` there are no
 * `OIDC_*` env vars, so a client ID there would be silently dropped. */
const toLoginFields = (
  login: GatewayLogin
): readonly (readonly [string, string])[] => {
  switch (login.mode) {
    case "gcip":
      return [
        ["loginMode", "gcip"],
        ["gcipApiKey", login.apiKey],
        ["gcipProjectId", login.projectId],
        ["gcipTenantId", login.tenantId],
      ];
    case "oidc":
      // Okta and Entra often omit `email_verified` from the ID token, which the
      // chart's default check turns into a rejected login for every user.
      if (login.oidcIssuer && login.oidcIssuer !== GOOGLE_OIDC_ISSUER) {
        return [
          ["oidcClientId", login.oidcClientId],
          ["oidcIssuer", login.oidcIssuer],
          ["oidcRequireEmailVerified", "false"],
        ];
      } else {
        return [["oidcClientId", login.oidcClientId]];
      }
    default:
      return assertNever(login);
  }
};

/** The `federatedIssuerAllowlist` line as it appears in the gateway module's
 * values block, for a given set of issuers. */
export const federatedIssuerAllowlistTerraformLine = (
  issuers: string[]
): string => `federatedIssuerAllowlist = "${issuers.join(",")}"`;

/** Terraform for deploying the gateway via the `p0-ai-gateway-stack/kubernetes`
 * module, pre-filled with everything known by the end of page 1 of the
 * install form. */
export const gatewayDeploymentTerraform = (
  data: GatewayTerraformInput
): string => {
  const {
    gatewayId,
    orgSlug,
    url,
    letsEncryptEmail,
    login,
    storage,
    p0Url,
    p0Audience,
    serviceAccountEmail,
    kubernetesNamespace = DEFAULT_KUBERNETES_NAMESPACE,
    federatedIssuers,
    tunnelHost,
  } = data;
  const host = new URL(url).host;

  // The chart defaults `p0Audience` to `p0Url`, so it is emitted only when the
  // caller supplies one. The same line goes in both blocks that take it.
  const p0AudienceField: readonly (readonly [string, string])[] = p0Audience
    ? [["p0Audience", p0Audience]]
    : [];

  // `loginMode` is omitted under "oidc" — it's the chart's own default, so
  // emitting it would churn existing installs' Terraform for no behavior change.
  const authServer = renderAlignedFields(AUTH_SERVER_INDENT, [
    ...toLoginFields(login),
    ["p0Url", p0Url],
    ...p0AudienceField,
  ]);

  // The chart defaults `tunnelHost` to the production API host, so only other
  // environments need the line.
  const braekhus = renderAlignedFields(AUTH_SERVER_INDENT, [
    ["orgSlug", orgSlug],
    ["gatewayId", gatewayId],
    ...(tunnelHost === DEFAULT_TUNNEL_HOST
      ? []
      : [["tunnelHost", tunnelHost] as const]),
  ]);

  const accessProxy = renderAlignedFields(AUTH_SERVER_INDENT, [
    ["p0Url", p0Url],
    ...p0AudienceField,
    ["manageAllowedEmails", serviceAccountEmail],
  ]);

  // The chart only sets FEDERATED_ISSUER_ALLOWLIST when this value is
  // non-empty, and an empty allowlist is its default, so emitting `= ""`
  // changes nothing and leaves a reader wondering what it controls.
  const allowlist =
    federatedIssuers.length > 0
      ? `\n${AUTH_SERVER_INDENT}${federatedIssuerAllowlistTerraformLine(federatedIssuers)}`
      : "";

  return `module "p0_agentic_gateway" {
  source        = "p0-security/p0-ai-gateway-stack/kubernetes"
  version       = "${AGENTIC_GATEWAY_MODULE_VERSION}"
  chart_version = "${AGENTIC_GATEWAY_CHART_VERSION}"

  release_name     = "${gatewayId}"
  namespace        = "${kubernetesNamespace}"
  create_namespace = true

  values = [yamlencode({
    letsEncrypt = {
      env   = "prod"
      email = "${letsEncryptEmail}"
    }
    postgresql = {
      ${renderStorage(storage)}
    }
    victorialogs = {
      ${renderStorage(storage)}
    }
    valkey = {
      ${renderValkey(storage)}
    }
    ai-gateway = {
      gateway = {
        className = "${gatewayId}"
        host      = "${host}"
      }
      braekhus = {
${braekhus}
      }
      authServer = {
${authServer}${allowlist}${renderCaches(storage)}
      }
      accessProxy = {
${accessProxy}
      }
    }
  })]
}`;
};
